import React, {
  RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import isNil from 'lodash/isNil';
import defer from 'lodash/defer';
import Button from 'pages/Dashboard/components/Button';
import {
  PatientDTO,
  RefundMethodOptionDTO,
} from 'dtos';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import {
  useConfirmDialog,
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  validatePatient,
} from 'pages/Dashboard/utils/helper';
import {
  useGetBillingSummary,
  useGetPatientEncountersWithPaymentInfo,
  useAdjustPayments,
  useGetRefundMethods,
  AdjustPaymentPayload,
} from 'views/EMR/hooks/billing';
import Table from 'core/components/VirtualTable';
import {
  getColumns,
} from 'views/EMR/Billing/AdjustPayment/MoveToCreditColumns';
import {
  appointmentsEventChannel,
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  formatMoney,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import WithdrawIcon from 'core/icons/withdraw';
import AmountSummary from 'views/EMR/Billing/AmountSummary';
import DepositIcon from 'core/icons/deposit';
import ArrowRightIcon from '@mui/icons-material/ArrowRightAltRounded';
import {
  ColumnDef,
} from '@tanstack/react-table';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';
import Title from 'views/EMR/Billing/AdjustPayment/Title';
import Select from 'core/components/Select';
import compact from 'lodash/compact';
import clsx from 'clsx';
import {
  getTotalAdjustedAmount,
  getAdjustmentError,
  getEncountersWithPaymentInfo,
  serializeRefundPayload,
  serializeMoveToCreditPayload,
} from 'views/EMR/Billing/AdjustPayment/utils';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import Input from 'pages/Dashboard/components/Input';
import InfoIcon from 'core/icons/info';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';

export type RefundPaymentMethod = {
  refundMethod: RefundMethodOptionDTO | null;
  transactionIdentifier: string | null;
};

export type AdjustmentFormData = {
  patient?: PatientDTO;
  encounters?: EncounterWithPayment[] | null;
  comment?: string;
  paymentMethod?: RefundPaymentMethod;
};

type Props = {
  onClose: () => void;
  onExpand: () => void;
  dialogRef: RefObject<HTMLDivElement>;
  moveToCreditFormRef: RefObject<HTMLDivElement>;
  adjustmentType: number;
  setAdjustmentType: (index: number) => void;
  hideNavigation?: boolean;
  shouldLockPatient?: boolean;
  defaultTitle?: string;
};

function RefundMethodFields() {
  const refundMethods = useGetRefundMethods();
  const { control, watch, setValue } = useFormContext();
  const selectedPaymentMethod = watch('paymentMethod');

  const discriminator = selectedPaymentMethod?.refundMethod?.discriminator ?? '';

  const isCheck = /^check/i.test(discriminator);
  const isCard = /^manualCardRefund/i.test(discriminator);

  useEffect(() => {
    const hasTransactionId = /^(card|check)/i.test(discriminator);
    const resetTransactionId = !isNil(selectedPaymentMethod?.refundMethod) && !hasTransactionId;

    if (resetTransactionId) {
      setValue('transactionIdentifier', null);
    }
  }, [selectedPaymentMethod?.refundMethod, discriminator]);

  return (
    <div className="flex w-full gap-form-field">
      <Controller
        control={control}
        name="paymentMethod.refundMethod"
        rules={{ required: 'Select refund method' }}
        render={({ field, fieldState: { invalid } }) => (
          <Select<RefundMethodOptionDTO>
            {...field}
            getValue={(option) => compact([option?.discriminator, option?.name]).join(' - ')}
            getLabel={(option) => option.name}
            id="payment-method"
            label="Refund method"
            required
            placeholder="Select"
            showErrorText={false}
            options={refundMethods ?? []}
            error={invalid}
            className="w-1/2"
          />
        )}
      />
      {(isCheck || isCard) && (
        <Controller
          name="paymentMethod.transactionIdentifier"
          rules={{
            required: isCheck
              ? 'Check number is required.'
              : 'Transaction ID is required.',
          }}
          control={control}
          render={
             ({ field, fieldState: { invalid } }) => (
               <Input
                 {...field}
                 required
                 error={invalid}
                 showErrorText={false}
                 className="w-1/2"
                 classes={{ container: '!h-8' }}
                 label={isCheck ? 'Check Number' : 'Transaction ID'}
                 placeholder={isCheck ? 'Check number' : 'Transaction ID'}
                 id="transaction-id"
               />
             )
           }
        />
      )}
    </div>
  );
}

export default function AdjustmentForm({
  onClose,
  onExpand,
  dialogRef,
  moveToCreditFormRef,
  adjustmentType,
  setAdjustmentType,
  hideNavigation,
  shouldLockPatient,
  defaultTitle,
}: Props) {
  const mixPanelTrack = useMixpanelTrack();

  const isMoveToCredit = adjustmentType === 0;
  const isRefund = adjustmentType === 1;

  const patientLookupInputRef = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
    watch,
    setValue,
  } = useFormContext<AdjustmentFormData>();

  const patient = watch('patient');
  const encounters = watch('encounters') ?? [];
  const transactionIdentifier = watch('paymentMethod.transactionIdentifier');
  const refundMethod = watch('paymentMethod.refundMethod');
  const discriminator = refundMethod?.discriminator ?? '';

  const {
    data,
    isLoading,
  } = useGetPatientEncountersWithPaymentInfo(patient?.patientId ?? 0);

  const {
    data: billingSummary,
  } = useGetBillingSummary({ patientId: patient?.patientId, isDeposit: true });

  const {
    mutateAsync: adjustPayments,
  } = useAdjustPayments(isMoveToCredit);

  const isSquareRefund = useMemo(
    () => isRefund && /SquareRefund/i.test(discriminator ?? ''),
    [discriminator, isRefund],
  );

  const totalAmountToAdjust = useMemo(
    () => getTotalAdjustedAmount(encounters, isMoveToCredit, isSquareRefund),
    [encounters, isMoveToCredit, isSquareRefund],
  );

  const error = useMemo(
    () => getAdjustmentError(
      encounters,
      patient ?? null,
      isMoveToCredit,
      isSquareRefund,
      {
        refundMethod: refundMethod ?? null,
        transactionIdentifier: transactionIdentifier ?? null,
      },
    ),
    [
      encounters,
      patient,
      isMoveToCredit,
      isSquareRefund,
      refundMethod,
      transactionIdentifier,
    ],
  );

  const encountersWithPaymentInfo = useMemo(
    () => getEncountersWithPaymentInfo(
      data ?? [],
      encounters,
      billingSummary ?? {},
      isMoveToCredit,
      isSquareRefund,
    ),
    [
      encounters,
      data,
      billingSummary,
      isMoveToCredit,
      isSquareRefund,
    ],
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  const onSubmit = async (formData: AdjustmentFormData) => {
    const payload = isMoveToCredit
      ? serializeMoveToCreditPayload(formData)
      : serializeRefundPayload(formData);

    try {
      await adjustPayments(payload as AdjustPaymentPayload);

      notify({
        message: isMoveToCredit
          ? `${formatMoney(totalAmountToAdjust)} has been moved to patient credit.`
          : `${formatMoney(totalAmountToAdjust)} has been refunded to the patient.`,
        variant: 'success',
      });

      encounters?.forEach(({ encounterId }) => (
        appointmentsEventChannel.emit('onEventUpdated', encounterId)));
      paymentsEventChannel.emit('onPaymentsDataChanged', 'patient-payments');
      paymentsEventChannel.emit('onPatientBalanceChange');

      onClose();
      if (isMoveToCredit) {
        confirm({
          title: 'Allocate Credit',
          content: (
            `${formatMoney(totalAmountToAdjust)} have been moved to patient's credit. Would you like to allocate the available credit now?`
          ),
          onConfirm: () => {
            billingEventChannel.emit('onEncountersAllocation', { patient: patient! });
            closeConfirm();
          },
        });
        mixPanelTrack('SubmitMoveToCredit');
      } else {
        mixPanelTrack('SubmitRefund');
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    defer(() => {
      if (isNil(patient)) {
        patientLookupInputRef?.current?.focus?.();
      }
    });
  }, [patient]);

  const updateData = (
    index: number,
    id: string,
    value: number | string,
    original: EncounterWithPayment,
  ) => {
    const amount = Number(value);
    const otherEncounters = encounters.filter(
      (enc) => (enc.encounterId !== original.encounterId),
    );

    setValue(
      'encounters',
      Number.isNaN(amount)
        ? otherEncounters
        : [...otherEncounters, { ...original, [id]: amount } as EncounterWithPayment],
      { shouldValidate: true },
    );
  };

  const columns = useMemo(() => getColumns(
    adjustmentType,
    isSquareRefund,
  ), [adjustmentType, isSquareRefund]);

  return (
    <div className="flex flex-col h-full w-full flex-1 !max-h-[calc(100vh-5rem)] overflow-y-hidden" ref={moveToCreditFormRef}>
      <Title
        active={adjustmentType}
        onChange={setAdjustmentType}
        onClose={handleClose}
        hideNavigation={hideNavigation}
        defaultTitle={defaultTitle}
      />
      <div className={clsx('pb-4 flex-1 flex flex-col h-full w-full gap-4', {
        'min-h-[200px] 2xl:min-h-[420px]': isRefund,
      })}
      >
        <div className="flex flex-col flex-1 gap-form-row populate-form overflow-y-auto px-4 ">
          <Controller
            name="patient"
            control={control}
            rules={{
              required: 'Select patient',
              validate: validatePatient,
            }}
            render={({ field, fieldState: { invalid } }) => (
              <PatientSelect
                {...field}
                value={field.value as PatientCompact ?? {}}
                className="!mb-0"
                label="Patient"
                error={invalid}
                id="patient-lookup"
                required
                readonly={shouldLockPatient}
                placeholder="Enter patient name, phone or date of birth"
                allowDefault
                inputRef={patientLookupInputRef as RefObject<HTMLInputElement>}
                containerRef={dialogRef}
              />
            )}
          />
          {!isNil(patient) && (
            <>
              <div className="flex w-full justify-between items-end">
                <div className="flex gap-4">
                  <AmountSummary
                    value={billingSummary?.credit ?? 0}
                    title="Patient's Credit"
                    Icon={DepositIcon}
                    variant="patientCredit"
                    textSize="medium"
                  />
                  <AmountSummary
                    value={totalAmountToAdjust}
                    title={`Total ${isMoveToCredit ? 'to Move' : 'Refund Amount'}`}
                    Icon={WithdrawIcon}
                    variant="patientDue"
                    textSize="medium"
                  />
                </div>
                <Button
                  variant="text"
                  color="primary"
                  onClick={onExpand}
                >
                  Past
                  {isMoveToCredit ? ' Credit Transfers ' : ' Refunds '}
                  <ArrowRightIcon className="!w-4 !h-4 !text-inherit" />
                </Button>
              </div>
              {!isMoveToCredit && (
                <div className="flex flex-col">
                  <RefundMethodFields />
                  {isSquareRefund && (
                  <div className="flex items-center gap-1">
                    <InfoIcon className="!w-3 !h-3 !text-gray-700" />
                    <span className="text-gray-500 text-[11px] font-medium">
                      Patient’s credits cannot be refunded through Square.
                    </span>
                  </div>
                  )}
                </div>
              )}
              {(isMoveToCredit || !isNil(refundMethod)) && (
                <div>
                  <div className="flex relative w-full h-auto bg-gray-50 overflow-hidden">
                    <div className={clsx('flex flex-1 flex-col w-full move-to-credit-table min-h-[150px]', {
                      'max-h-[calc(100vh-25rem)]': isMoveToCredit,
                      'max-h-[calc(100vh-40rem)]': !isMoveToCredit,
                    })}
                    >
                      <Table<EncounterWithPayment>
                        columns={columns as ColumnDef<EncounterWithPayment>[]}
                        data={encountersWithPaymentInfo ?? []}
                        updateData={updateData}
                        loading={isLoading}
                        skeleton={{
                          rows: 3,
                        }}
                        emptySlot={isMoveToCredit
                          ? 'No encounters have payments that can be moved to credit.'
                          : `No encounters have payments that can be refunded${isSquareRefund ? ' through Square' : ''}.`}
                        classes={{
                          body: '!m-0 !p-0 !bg-white',
                          scrollContainer: '!p-0',
                          column: '!text-left',
                          outerRow: 'move-to-credit-table-row !w-full',
                          outerHeader: '!bg-gray-100 !border !border-gray-200 !rounded-sm',
                          header: '!text-gray-700 !py-0 !border-0',
                          scrollbarGutter: 'inherit',
                          windowsClassName: '!w-full',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isRefund && !isNil(refundMethod) && (
                <Controller
                  control={control}
                  name="comment"
                  render={({ field }) => (
                    <TextAreaWithActions
                      {...field}
                      placeholder="Add comment"
                      rows={3}
                      id="refund-comment"
                      trackingLabel="Refund Comment"
                      maxLength={512}
                    />
                  )}
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center px-4 pb-4">
        <Button
          variant="outlined"
          type="button"
          onClick={handleClose}
        >
          Close
        </Button>
        <ToolTip
          title={error}
          hide={isNil(error)}
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || !isNil(error) || isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              Confirm
            </Button>
          </span>
        </ToolTip>
      </div>
    </div>
  );
}
